import React, { useState } from "react";
import axios from "axios";
import { Modal, notification, message } from "antd";
import { STATUS_CONFIG } from "./statusPaqueteConfig";


// Ya no pasan por "Orden de entrega"(3) ni "En tránsito"(4) 
// van directo de Empaquetado(2) a Entregado(5). 
export const ORIGENES_FLUJO_REDUCIDO = ["envio_manual", "entrega_sucursal"];

export const TIPO_ENVIO_LABELS = {
  bodega_local: "Automático",
  envio_manual: "Manual",
  entrega_sucursal: "Sucursal",
  dropshipping_urrea: "Dropshipping",
};

export const ORIGENES_FILTRO = [
  { key: "bodega_local", label: TIPO_ENVIO_LABELS.bodega_local },
  { key: "envio_manual", label: TIPO_ENVIO_LABELS.envio_manual },
  { key: "entrega_sucursal", label: TIPO_ENVIO_LABELS.entrega_sucursal },
  { key: "dropshipping_urrea", label: TIPO_ENVIO_LABELS.dropshipping_urrea },
];

// Estados visibles en el tablero / disponibles como destino
// de transición, según el origen.
export const getEstadosVisibles = (origenKey) =>
  ORIGENES_FLUJO_REDUCIDO.includes(origenKey)
    ? ["0", "1", "2", "5"]
    : Object.keys(STATUS_CONFIG);

// Reglas de secuencia de transición: por defecto solo se puede avanzar
// un estado a la vez (estadoDestino === estadoActual + 1). Los orígenes
// de flujo reducido pueden saltar directo de Empaquetado(2) a Entregado(5).
export const esTransicionValida = (origenKey, estadoActual, estadoDestino) => {
  const esSaltoFlujoReducido =
    ORIGENES_FLUJO_REDUCIDO.includes(origenKey) &&
    estadoActual === 2 &&
    estadoDestino === 5;

  return esSaltoFlujoReducido || estadoDestino === estadoActual + 1;
};


// Acción de VentasEcommerceTabla para decidir qué botón mostrar.
export const determinarAccionesDisponibles = (venta, envio, origenKey) => {
  const estadoActual = envio.estadoPaquete;
  const estadosVisibles = getEstadosVisibles(origenKey).map(Number);
  const siguiente = estadosVisibles.find((estado) =>
    esTransicionValida(origenKey, estadoActual, estado),
  );

  if (siguiente === undefined) return null;

  // "Pedido creado" -> "Pedido pagado" lo confirma la pasarela de pago
  if (siguiente === 1) return null;

  // Para dropshipping la entrega se confirma mediante el webhook no mediante el botón de "Entregado"
  if (siguiente === 5 && !ORIGENES_FLUJO_REDUCIDO.includes(origenKey)) return null;

  const insumosSinEmpaquetar = (venta.insumos || []).filter(
    (ins) => !ins.servicio && !ins.dropshipping && !ins.libroVirtual && !ins.empaquetado,
  );

  const esDropshipping = origenKey === "dropshipping_urrea";
  const insumosDelPaquete = (venta.insumos || []).filter(
    (ins) => !!ins.dropshipping === esDropshipping && !ins.libroVirtual,
  );
  const todosEntregados = insumosDelPaquete.every(
    (ins) => (ins.entregado || 0) >= ins.cantidad,
  );

  return {
    estadoActual,
    estadoDestino: siguiente,
    label: STATUS_CONFIG[siguiente]?.label || "Avanzar",
    requiereConfirmarOC: esDropshipping && siguiente === 3,
    advertenciaInsumosSinEmpaquetar:
      siguiente === 3 && insumosSinEmpaquetar.length > 0
        ? insumosSinEmpaquetar.length
        : 0,
    requiereSalidaInventario:
      (siguiente === 4 || siguiente === 5) && !todosEntregados,
  };
};

// Hook con acciones y modales compartidos entre ambas vistas
const useVentasEcommerceAcciones = ({ empresaId, headersToken, history, onRefrescar }) => {
  const [correosConfig, setCorreosConfig] = useState({});
  const [envioManualActivo, setEnvioManualActivo] = useState(false);

  const [modalGuiaVisible, setModalGuiaVisible] = useState(false);
  const [modalGuiaManualVisible, setModalGuiaManualVisible] = useState(false);
  const [modalOCVisible, setModalOCVisible] = useState(false);
  const [selectedVentaId, setSelectedVentaId] = useState(null);
  const [selectedOrigenKey, setSelectedOrigenKey] = useState(null);
  const [soloActualizarEmpaquetado, setSoloActualizarEmpaquetado] = useState(false);
  const [modalPreviewVisible, setModalPreviewVisible] = useState(false);
  const [archivoPreview, setArchivoPreview] = useState(null);
  const [modalRecoleccionVisible, setModalRecoleccionVisible] = useState(false);
  const [recoleccionCarrier, setRecoleccionCarrier] = useState(null);
  const [recoleccionOrigen, setRecoleccionOrigen] = useState(null);
  const [modalTransitoVisible, setModalTransitoVisible] = useState(false);
  const [transitoVenta, setTransitoVenta] = useState(null);
  const [transitoEnvio, setTransitoEnvio] = useState(null);
  const [transitoOrigenKey, setTransitoOrigenKey] = useState(null);
  const [modalEstadoCorreoVisible, setModalEstadoCorreoVisible] = useState(false);
  const [estadoCorreoVenta, setEstadoCorreoVenta] = useState(null);
  const [estadoCorreoEnvio, setEstadoCorreoEnvio] = useState(null);
  const [estadoCorreoOrigen, setEstadoCorreoOrigen] = useState(null);
  const [estadoCorreoNuevoEstado, setEstadoCorreoNuevoEstado] = useState(null);
  const [modalEmpaqueSucursalVisible, setModalEmpaqueSucursalVisible] = useState(false);
  const [modalConfirmarTransferenciaVisible, setModalConfirmarTransferenciaVisible] = useState(false);
  const [ventaAConfirmarTransferencia, setVentaAConfirmarTransferencia] = useState(null);
  const [modalConciliarExcelVisible, setModalConciliarExcelVisible] = useState(false);
  const [modalPickingLoteVisible, setModalPickingLoteVisible] = useState(false);
  const [modalEstadoGeneralVisible, setModalEstadoGeneralVisible] = useState(false);
  const [estadoGeneralVentaId, setEstadoGeneralVentaId] = useState(null);

  const refrescar = () => {
    if (onRefrescar) onRefrescar();
  };

  const consultarConfiguracionCorreos = () => {
    axios
      .post(
        "configuracionTiendaOnline/obtenerConfiguracionCorreos",
        { empresaId },
        { headers: headersToken },
      )
      .then((res) => {
        if (res.data.success === 2 && res.data.data) {
          setCorreosConfig(res.data.data);
        }
      })
      .catch(() => {});
  };

  const consultarConfiguracionEnvioManual = () => {
    axios
      .post(
        "configuracionTiendaOnline/obtenerConfiguracionApis",
        { empresaId },
        { headers: headersToken },
      )
      .then((res) => {
        if (res.data.success === 2 && res.data.data) {
          setEnvioManualActivo(!!res.data.data.envioManual?.activo);
        }
      })
      .catch(() => {});
  };

  const getEnviarCorreoDefault = (estado, origenKey) => {
    const esSucursal = origenKey === "entrega_sucursal";
    const key = `estado${parseInt(estado)}${esSucursal ? "Sucursal" : ""}`;
    const cfg = correosConfig?.estadoPedido?.[key];
    return cfg?.enviarPorDefecto !== false;
  };

  const verificarPagoVenta = (ventaId) => {
    return axios
      .post(
        "ventasEcommerce/verificarPagoConekta",
        { ventaId, empresaId },
        { headers: headersToken },
      )
      .then((res) => {
        const data = res.data || {};
        if (!data.success) {
          notification.error({
            message: "No se pudo verificar el pago",
            description: data.message || "Inténtalo de nuevo en unos minutos.",
          });
          return;
        }
        if (data.paid) {
          notification.success({
            message: data.alreadyApplied
              ? "El pago ya estaba registrado"
              : "Pago confirmado",
            description: data.message,
          });
          refrescar();
        } else {
          notification.info({
            message: "Pago aún pendiente",
            description: data.message || "Vuelve a intentar en unos minutos.",
          });
        }
      })
      .catch((err) => {
        console.error("Error verificando pago Conekta:", err);
        notification.error({
          message: "Error verificando el pago",
          description: "No se pudo comunicar con el servidor.",
        });
      });
  };

  const verificarPagoMattildaVenta = (ventaId) => {
    return axios
      .post(
        "ventasEcommerce/verificarPagoMattilda",
        { ventaId, empresaId },
        { headers: headersToken },
      )
      .then((res) => {
        const data = res.data || {};
        if (!data.success) {
          notification.error({
            message: "No se pudo verificar el pago",
            description: data.message || "Inténtalo de nuevo en unos minutos.",
          });
          return;
        }
        if (data.paid) {
          notification.success({
            message: data.alreadyApplied
              ? "El pago ya estaba registrado"
              : "Pago confirmado",
            description: data.message,
          });
          refrescar();
        } else {
          notification.info({
            message: "Pago aún pendiente",
            description: data.message || "Vuelve a intentar en unos minutos.",
          });
        }
      })
      .catch((err) => {
        console.error("Error verificando pago Mattilda:", err);
        notification.error({
          message: "Error verificando el pago",
          description: "No se pudo comunicar con el servidor.",
        });
      });
  };

  const pagarManualmente = (ventaId) => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: "Confirmar pago",
        content:
          "Se marcará esta venta como pagada de forma manual. Se generará la póliza y el pago correspondiente, y se notificará al cliente. ¿Deseas continuar?",
        okText: "Sí, confirmar pago",
        cancelText: "Cancelar",
        onCancel: () => resolve(),
        onOk: () =>
          axios
            .post(
              "ventasEcommerce/pagarManualmente",
              { ventaId, empresaId },
              { headers: headersToken },
            )
            .then((res) => {
              const data = res.data || {};
              if (!data.success) {
                notification.error({
                  message: "No se pudo aplicar el pago",
                  description:
                    data.message || "Inténtalo de nuevo en unos minutos.",
                });
                return;
              }
              notification.success({
                message: data.alreadyApplied
                  ? "El pago ya estaba registrado"
                  : "Pago confirmado",
                description: data.message,
              });
              refrescar();
            })
            .catch((err) => {
              console.error("Error aplicando pago manual:", err);
              notification.error({
                message: "Error aplicando el pago",
                description: "No se pudo comunicar con el servidor.",
              });
            })
            .finally(() => resolve()),
      });
    });
  };

  const confirmarPagoTransferencia = (venta) => {
    if (!venta) return;
    setModalConfirmarTransferenciaVisible(true);
    setVentaAConfirmarTransferencia(venta);
  };

  const reenviarCorreoEstado = (venta, envio) => {
    const estado = envio.estadoPaquete ?? 0;
    const labelEstado = STATUS_CONFIG[estado]?.label || "este estado";
    const nombreCliente =
      `${venta.clienteOnline?.nombre || ""} ${
        venta.clienteOnline?.apellidoPaterno || ""
      }`.trim() ||
      venta.clienteOnline?.email ||
      "el cliente";

    return new Promise((resolve) => {
      Modal.confirm({
        title: "¿Reenviar correo al cliente?",
        content: `Se volverá a enviar el correo de "${labelEstado}" a ${nombreCliente} (${
          venta.clienteOnline?.email || "sin correo"
        }).`,
        okText: "Sí, reenviar",
        cancelText: "Cancelar",
        onCancel: () => resolve(),
        onOk: () =>
          axios
            .post(
              "ventasEcommerce/reenviarCorreoEstado",
              { ventaId: venta._id, empresaId, origenKey: envio.origen, estado },
              { headers: headersToken },
            )
            .then((res) => {
              if (res.data.success === 1) {
                notification.success({
                  message: "Correo reenviado",
                  description: res.data.message,
                });
              } else {
                notification.error({
                  message: "No se pudo reenviar el correo",
                  description: res.data.message,
                });
              }
            })
            .catch(() =>
              notification.error({
                message: "Error de conexión",
                description: "No se pudo comunicar con el servidor.",
              }),
            )
            .finally(() => resolve()),
      });
    });
  };

  const actualizarEstadoBD = (ventaId, origenKey, nuevoEstado) => {
    return axios
      .post(
        "ventasEcommerce/actualizarEstadoPaquete",
        { ventaId, origenKey, nuevoEstado, empresaId },
        { headers: headersToken },
      )
      .then((res) => {
        if (res.data.success === 1) {
          message.success("Estado actualizado correctamente");
          refrescar();
        } else {
          notification.error({ message: "No se pudo actualizar el estado" });
        }
      })
      .catch(() =>
        notification.error({ message: "Error al comunicar con el servidor" }),
      );
  };

  // Limita a máximo 200 items por petición para no saturar el servidor ni el navegador
  const CHUNK_SIZE_LOTE = 200;

  const actualizarEstadoLote = async (items, origenKey, nuevoEstado) => {
    if (!items || items.length === 0) return null;

    const chunks = [];
    for (let i = 0; i < items.length; i += CHUNK_SIZE_LOTE) {
      chunks.push(items.slice(i, i + CHUNK_SIZE_LOTE));
    }

    const reporteCompleto = [];
    for (const chunk of chunks) {
      try {
        const res = await axios.post(
          "ventasEcommerce/actualizarEstadoPaqueteLote",
          { empresaId, origenKey, nuevoEstado, enviarCorreo: true, items: chunk },
          { headers: headersToken },
        );
        if (res.data.success === 1 && Array.isArray(res.data.reporte)) {
          reporteCompleto.push(...res.data.reporte);
        } else {
          chunk.forEach((it) =>
            reporteCompleto.push({
              ventaId: it.ventaId,
              success: false,
              message: res.data.message || "Error al procesar el lote",
            }),
          );
        }
      } catch (err) {
        chunk.forEach((it) =>
          reporteCompleto.push({
            ventaId: it.ventaId,
            success: false,
            message: "Error de conexión con el servidor",
          }),
        );
      }
    }

    const exitosos = reporteCompleto.filter((r) => r.success).length;
    const fallidos = reporteCompleto.filter((r) => !r.success);

    if (fallidos.length === 0) {
      notification.success({
        message: "Lote actualizado",
        description: `${exitosos} de ${reporteCompleto.length} pedido(s) actualizado(s) correctamente.`,
      });
    } else {
      mostrarDetalleFallidosLote(fallidos, exitosos, reporteCompleto.length);
    }

    refrescar();
    return { exitosos, fallidos, reporte: reporteCompleto };
  };

  const mostrarDetalleFallidosLote = (fallidos, exitosos, total) => {
    Modal.info({
      title: "Pedidos que no se pudieron actualizar",
      width: 600,
      content: (
        <div>
          <p>
            <strong>{exitosos}</strong> de <strong>{total}</strong> pedido(s) se actualizaron
            correctamente. Los siguientes <strong>{fallidos.length}</strong> no:
          </p>
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {fallidos.map((f, idx) => (
              <div key={idx} style={{ marginBottom: 6 }}>
                <strong>{f.serieNumero || f.ventaId}</strong>: {f.message}
              </div>
            ))}
          </div>
        </div>
      ),
    });
  };

  const seguirEmpaquetando = (venta, envio) => {
    if (envio.origen === "entrega_sucursal") {
      setModalEmpaqueSucursalVisible(true);
      setSelectedVentaId(venta._id);
      setSelectedOrigenKey(envio.origen);
      setSoloActualizarEmpaquetado(true);
    } else if (envio.origen === "envio_manual") {
      setModalGuiaManualVisible(true);
      setSelectedVentaId(venta._id);
      setSelectedOrigenKey(envio.origen);
      setSoloActualizarEmpaquetado(true);
    }
  };

  const abrirPrevisualizacionGuia = (envio, serieNumero) => {
    if (!envio.labelUrl) return;
    setModalPreviewVisible(true);
    setArchivoPreview({
      url: envio.labelUrl,
      name: `Guia_${envio.carrier}_${serieNumero}.pdf`,
      mime: "application/pdf",
    });
  };

  return {
    correosConfig,
    envioManualActivo,
    consultarConfiguracionCorreos,
    consultarConfiguracionEnvioManual,
    getEnviarCorreoDefault,
    verificarPagoVenta,
    verificarPagoMattildaVenta,
    pagarManualmente,
    confirmarPagoTransferencia,
    reenviarCorreoEstado,
    actualizarEstadoBD,
    actualizarEstadoLote,
    seguirEmpaquetando,
    abrirPrevisualizacionGuia,

    modales: {
      modalGuiaVisible, setModalGuiaVisible,
      modalGuiaManualVisible, setModalGuiaManualVisible,
      modalOCVisible, setModalOCVisible,
      selectedVentaId, setSelectedVentaId,
      selectedOrigenKey, setSelectedOrigenKey,
      soloActualizarEmpaquetado, setSoloActualizarEmpaquetado,
      modalPreviewVisible, setModalPreviewVisible,
      archivoPreview, setArchivoPreview,
      modalRecoleccionVisible, setModalRecoleccionVisible,
      recoleccionCarrier, setRecoleccionCarrier,
      recoleccionOrigen, setRecoleccionOrigen,
      modalTransitoVisible, setModalTransitoVisible,
      transitoVenta, setTransitoVenta,
      transitoEnvio, setTransitoEnvio,
      transitoOrigenKey, setTransitoOrigenKey,
      modalEstadoCorreoVisible, setModalEstadoCorreoVisible,
      estadoCorreoVenta, setEstadoCorreoVenta,
      estadoCorreoEnvio, setEstadoCorreoEnvio,
      estadoCorreoOrigen, setEstadoCorreoOrigen,
      estadoCorreoNuevoEstado, setEstadoCorreoNuevoEstado,
      modalEmpaqueSucursalVisible, setModalEmpaqueSucursalVisible,
      modalConfirmarTransferenciaVisible, setModalConfirmarTransferenciaVisible,
      ventaAConfirmarTransferencia, setVentaAConfirmarTransferencia,
      modalConciliarExcelVisible, setModalConciliarExcelVisible,
      modalPickingLoteVisible, setModalPickingLoteVisible,
      modalEstadoGeneralVisible, setModalEstadoGeneralVisible,
      estadoGeneralVentaId, setEstadoGeneralVentaId,
    },
  };
};

export default useVentasEcommerceAcciones;
